/**
 * API client for user and team management (D2-USR-001 through D2-USR-005).
 *
 * All endpoints require admin role. Mounted at /api/v1/manage/...
 */
import { apiRequest } from "./client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TeamSummary {
  id: number;
  name: string;
}

export interface TeamMembershipEntry {
  id: number;
  team_id: number;
  team_name: string;
}

/**
 * Named permission presets (D4-AUTH-001 / D5-RBP-001). These mirror the backend
 * `PermissionPreset` text choices — assigning one bulk-applies a flag bundle
 * server-side, so the admin UI never has to set the individual flags by hand.
 */
export type PermissionPreset =
  | "none"
  | "paralegal"
  | "finance"
  | "manager"
  | "accounts"
  | "finance_accounts"
  | "office_admin"
  | "hr"
  // D5-RBP-005 (#4175): combined HR + Paralegal desk (one login, both desks).
  | "hr_paralegal"
  | "canvasser"
  | "custom";

/** Plain, realtor-friendly labels for each preset (non-technical audience). */
export const PERMISSION_PRESET_LABELS: Record<PermissionPreset, string> = {
  none: "Agent (standard user)",
  paralegal: "Paralegal (Legal)",
  finance: "Finance",
  manager: "Manager",
  accounts: "Accounts",
  finance_accounts: "Finance/Accounts",
  office_admin: "Office Admin",
  hr: "HR / Compliance",
  hr_paralegal: "HR / Paralegal (both desks)",
  canvasser: "Canvasser (farm calling)",
  custom: "Custom",
};

/**
 * #2847 — Natalie's authoritative 8-rank hierarchy ("Ranking of users",
 * 30 Jul 2026): 1 Super-user · 2 Finance/Accounts · 3 Manager · 4 Paralegal ·
 * 5 HR/Compliance · 6 Office Admin · 7 Agent · 8 Canvasser. Mirrors the
 * backend `apps/users/hierarchy.py` PRESET_RANKS — the SERVER is
 * authoritative; the UI only avoids offering options the API would 403.
 */
export const PRESET_RANKS: Record<PermissionPreset, number> = {
  none: 7,
  custom: 7,
  paralegal: 4,
  finance: 2,
  manager: 3,
  accounts: 2,
  finance_accounts: 2,
  office_admin: 6,
  hr: 5,
  // D5-RBP-005 (#4175): the combined seat takes the more senior of its two
  // ranks (Paralegal 4 over HR 5) -- never higher.
  hr_paralegal: 4,
  canvasser: 8,
};

/** Rank of an actor from their role + preset (admin role => rank 1). */
export function rankFor(
  role: string | null | undefined,
  preset: string | null | undefined,
): number {
  if (role === "admin") return 1;
  return PRESET_RANKS[(preset ?? "none") as PermissionPreset] ?? 7;
}

/**
 * The presets an actor may assign: strictly LOWER rank than their own
 * (admins, rank 1, may assign everything). Mirrors the server-side
 * no-escalation guard; ordered high rank -> low for a stable picker.
 */
export function assignablePresetsFor(
  role: string | null | undefined,
  preset: string | null | undefined,
): PermissionPreset[] {
  const actorRank = rankFor(role, preset);
  const all = Object.keys(PRESET_RANKS) as PermissionPreset[];
  const allowed =
    actorRank === 1 ? all : all.filter((p) => PRESET_RANKS[p] > actorRank);
  // Stable picker order: "Agent (standard user)" first, then seniority
  // (rank ascending), with "Custom" last.
  const named = allowed
    .filter((p) => p !== "none" && p !== "custom")
    .sort((a, b) => PRESET_RANKS[a] - PRESET_RANKS[b]);
  return [
    ...(allowed.includes("none") ? (["none"] as PermissionPreset[]) : []),
    ...named,
    ...(allowed.includes("custom") ? (["custom"] as PermissionPreset[]) : []),
  ];
}

/**
 * #3784: the "role category" a person is shown under on the user list. The
 * hard `role` is only ever agent/admin, so a back-office desk or a canvasser
 * (a role=agent login carrying a named preset) used to read as plain "Agent".
 * The category folds the two together: admin role => "admin"; a NAMED preset
 * => that preset; no preset / "none" / "custom" => a plain "agent" (custom is
 * agent-ranked, per PRESET_RANKS).
 */
export type RoleCategory =
  | "admin"
  | "agent"
  | Exclude<PermissionPreset, "none" | "custom">;

/** Short pill labels for each role category (agency-neutral). */
export const ROLE_CATEGORY_LABELS: Record<RoleCategory, string> = {
  admin: "Admin",
  agent: "Agent",
  paralegal: "Paralegal",
  finance: "Finance",
  manager: "Manager",
  accounts: "Accounts",
  finance_accounts: "Finance/Accounts",
  office_admin: "Office Admin",
  hr: "HR / Compliance",
  hr_paralegal: "HR / Paralegal",
  canvasser: "Canvasser",
};

export function roleCategoryOf(user: {
  role: string;
  permission_preset?: string | null;
}): RoleCategory {
  if (user.role === "admin") return "admin";
  const preset = user.permission_preset;
  if (!preset || preset === "none" || preset === "custom") return "agent";
  return preset in ROLE_CATEGORY_LABELS ? (preset as RoleCategory) : "agent";
}

/**
 * #3784: the role-category options an actor may filter the user list by.
 * A scoped user-manager (rank 3/4/5) only ever lists users of strictly lower
 * rank (server-side queryset scoping), so it is only offered the categories it
 * can actually see; an admin sees every category.
 */
export function roleCategoryFilterOptions(
  role: string | null | undefined,
  preset: string | null | undefined,
): RoleCategory[] {
  const isAdmin = role === "admin";
  const presets = isAdmin
    ? (Object.keys(PRESET_RANKS) as PermissionPreset[])
    : assignablePresetsFor(role, preset);
  const named = presets
    .filter((p) => p !== "none" && p !== "custom")
    .sort((a, b) => PRESET_RANKS[a] - PRESET_RANKS[b]) as RoleCategory[];
  const agentVisible = isAdmin || presets.includes("none");
  return [
    ...(isAdmin ? (["admin"] as RoleCategory[]) : []),
    ...(agentVisible ? (["agent"] as RoleCategory[]) : []),
    ...named,
  ];
}

/**
 * #3784: translate a role-category filter into the user-list query params.
 * "agent" means a plain agent (role=agent with no named preset), so a
 * canvasser or back-office desk no longer lands under the Agent filter.
 */
export function roleCategoryQuery(
  category: RoleCategory | "",
): Pick<UserFilters, "role" | "permission_preset" | "permission_preset__in"> {
  if (!category) return {};
  if (category === "admin") return { role: "admin" };
  if (category === "agent") {
    return { role: "agent", permission_preset__in: "none,custom" };
  }
  return { role: "agent", permission_preset: category };
}

/** The individual permission flags an admin can toggle under the Custom preset. */
export const PERMISSION_FLAG_KEYS = [
  "capture_deal",
  "view_commission",
  "enter_own_commission",
  "authorize_commission",
  "process_payment",
  "sign_off_deals",
  "invest_funds",
  "manage_users",
  // #2813: restricted HR/personnel document-template capability -- a genuine
  // per-user grant (never bundled into a named preset), toggled here.
  "manage_hr_documents",
] as const;

export type PermissionFlagKey = (typeof PERMISSION_FLAG_KEYS)[number];

/** Plain labels for each individual flag, shown only when "Custom" is selected. */
export const PERMISSION_FLAG_LABELS: Record<PermissionFlagKey, string> = {
  capture_deal: "Capture deals / mandates",
  view_commission: "View commission figures",
  enter_own_commission: "Enter own commission",
  authorize_commission: "Authorise commission",
  process_payment: "Process payments",
  sign_off_deals: "Sign off deals / mandates",
  invest_funds: "Invest funds",
  manage_users: "Manage users (scoped, below own tier)",
  manage_hr_documents: "Manage HR / personnel document templates",
};

export interface ManagedUser {
  id: number;
  username: string;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  role: "agent" | "admin";
  phone: string;
  designation: string;
  is_active: boolean;
  /** D2-USR-007: test / demo / system / vendor login, hidden from agent pickers. */
  hide_from_agent_rosters?: boolean;
  /** D2-FRM-018: office canvasser -- whole farm book, no area ownership. */
  farm_wide_canvass?: boolean;
  farm_view_only: boolean;
  email_edit_permission: boolean;
  capture_deal: boolean;
  view_commission: boolean;
  enter_own_commission: boolean;
  authorize_commission: boolean;
  process_payment: boolean;
  sign_off_deals: boolean;
  invest_funds: boolean;
  manage_users: boolean;
  /** #2813: restricted HR/personnel document-template capability (per-user). */
  manage_hr_documents: boolean;
  permission_preset: PermissionPreset;
  team: number | null;
  team_name: string | null;
  team_memberships: TeamMembershipEntry[];
  date_joined: string;
  last_login: string | null;
}

export interface ManagedUserPayload {
  username?: string;
  email?: string;
  first_name?: string;
  last_name?: string;
  role?: "agent" | "admin";
  phone?: string;
  designation?: string;
  is_active?: boolean;
  /** D2-USR-007: hide this login from the agent-facing sales-agent pickers. */
  hide_from_agent_rosters?: boolean;
  /** D2-FRM-018: grant/revoke office-canvasser whole-farm-book access. */
  farm_wide_canvass?: boolean;
  farm_view_only?: boolean;
  email_edit_permission?: boolean;
  capture_deal?: boolean;
  view_commission?: boolean;
  enter_own_commission?: boolean;
  authorize_commission?: boolean;
  process_payment?: boolean;
  sign_off_deals?: boolean;
  invest_funds?: boolean;
  manage_users?: boolean;
  /** #2813: restricted HR/personnel document-template capability (per-user). */
  manage_hr_documents?: boolean;
  /** Write-only: apply a named preset's flag bundle server-side (D4-AUTH-001). */
  apply_preset?: PermissionPreset;
  team?: number | null;
  /**
   * EDIT only, Super-User break-glass (D2-USR-008). The create endpoint refuses
   * a password outright -- a new login is always invite-only.
   */
  password?: string;
  /** CREATE only: proceed past a name-only duplicate match (D2-USR-008). */
  allow_duplicate_name?: boolean;
}

/**
 * Outcome of issuing an account-setup invite (D2-USR-008). `setup_link` is
 * present ONLY when the e-mail did not really go out (outbound switched off,
 * audience-restricted, no provider, no address) -- a one-time, 7-day link the
 * admin passes on by hand.
 */
export interface InviteResult {
  delivered: boolean;
  reason: "sent" | "suppressed" | "mock" | "no_email" | "failed" | "has_password";
  detail: string;
  expires_at: string | null;
  setup_link?: string;
}

export interface CreatedUser extends ManagedUser {
  invite?: InviteResult;
}

/** One existing account the duplicate guard matched (HTTP 409, D2-USR-008). */
export interface DuplicateUserMatch {
  id: number;
  full_name: string;
  username: string;
  email: string;
  is_active: boolean;
  role: string;
  permission_preset: string;
  match: Array<"email" | "name">;
}

export interface DuplicateUserConflict {
  code: "duplicate_user";
  detail: string;
  matches: DuplicateUserMatch[];
  /** Some match exists that the caller may not manage (never named). */
  hidden_matches: boolean;
  /** A switched-off match the caller may reactivate instead of creating. */
  reactivate_candidate: number | null;
  /** True for a name-only clash (two people may share a name). */
  can_create_anyway: boolean;
}

/** Narrow an unknown error to the duplicate-user 409 body, if it is one. */
export function asDuplicateUserConflict(err: unknown): DuplicateUserConflict | null {
  if (!err || typeof err !== "object") return null;
  const e = err as { status?: number; data?: Record<string, unknown> };
  if (e.status === 409 && e.data && e.data.code === "duplicate_user") {
    return e.data as unknown as DuplicateUserConflict;
  }
  return null;
}

export interface Team {
  id: number;
  name: string;
  description: string;
  member_count: number;
  members: Array<{ id: number; username: string; full_name: string }>;
  created_at: string;
  updated_at: string;
}

export interface TeamPayload {
  name: string;
  description?: string;
}

export interface TeamMembership {
  id: number;
  team: number;
  user: number;
  user_name: string;
  team_name: string;
  created_at: string;
}

export interface AuditLogEntry {
  id: number;
  timestamp: string;
  user: number | null;
  user_name: string | null;
  action: "create" | "update" | "delete";
  model_name: string;
  record_id: string;
  changes: Record<string, { old: unknown; new: unknown }>;
}

export interface PaginatedResponse<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// User management API
// ---------------------------------------------------------------------------

export interface UserFilters {
  search?: string;
  role?: string;
  /** #3784: exact permission preset (e.g. "canvasser"). */
  permission_preset?: string;
  /** #3784: comma-separated preset list (e.g. "none,custom" = plain agents). */
  permission_preset__in?: string;
  is_active?: string;
  team?: number;
  page?: number;
  /**
   * Bounded page-size override (#1239). UserViewSet uses
   * CappedPageNumberPagination, so a caller that needs the full roster in one
   * request (e.g. the admin-only agent filter on /crm/listings) can raise this
   * up to 500 instead of being silently capped at the default 25.
   */
  page_size?: number;
  ordering?: string;
}

function buildQueryString(filters: object): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(filters)) {
    if (value !== undefined && value !== "") {
      params.set(key, String(value));
    }
  }
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export async function fetchUsers(
  filters: UserFilters = {},
): Promise<PaginatedResponse<ManagedUser>> {
  return apiRequest<PaginatedResponse<ManagedUser>>(
    `/manage/users/${buildQueryString(filters)}`,
  );
}

export async function fetchUser(id: number): Promise<ManagedUser> {
  return apiRequest<ManagedUser>(`/manage/users/${id}/`);
}

export async function createUser(data: ManagedUserPayload): Promise<CreatedUser> {
  return apiRequest<CreatedUser>("/manage/users/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateUser(
  id: number,
  data: ManagedUserPayload,
): Promise<ManagedUser> {
  return apiRequest<ManagedUser>(`/manage/users/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deactivateUser(id: number): Promise<void> {
  await apiRequest(`/manage/users/${id}/`, { method: "DELETE" });
}

/**
 * Admin: (re)send an account-setup / invite link to a user (#698).
 * POST /api/v1/manage/users/{id}/send-invite/
 */
export async function sendUserInvite(
  userId: number,
): Promise<{
  detail: string;
  mock_mode: boolean;
  purpose: string;
  /** #1084 -- false when the outbound-email kill-switch suppressed the send. */
  email_outbound_enabled?: boolean;
  suppressed?: boolean;
  /** D2-USR-008: true only when a real provider accepted the e-mail. */
  delivered?: boolean;
  /** D2-USR-008: the one-time setup link, when an INVITE was not delivered. */
  setup_link?: string;
}> {
  return apiRequest(`/manage/users/${userId}/send-invite/`, { method: "POST" });
}

/**
 * Switch a switched-off person back on (D2-USR-008). Rank-gated server-side
 * and AuditLogged; by default a fresh invite is issued as well.
 * POST /api/v1/manage/users/{id}/reactivate/
 */
export async function reactivateUser(
  userId: number,
  sendInvite = true,
): Promise<{ user: ManagedUser; invite: InviteResult | null }> {
  return apiRequest(`/manage/users/${userId}/reactivate/`, {
    method: "POST",
    body: JSON.stringify({ send_invite: sendInvite }),
  });
}

// ---------------------------------------------------------------------------
// Team management API
// ---------------------------------------------------------------------------

export async function fetchTeams(): Promise<PaginatedResponse<Team>> {
  return apiRequest<PaginatedResponse<Team>>("/manage/teams/?page_size=100");
}

export async function fetchTeam(id: number): Promise<Team> {
  return apiRequest<Team>(`/manage/teams/${id}/`);
}

export async function createTeam(data: TeamPayload): Promise<Team> {
  return apiRequest<Team>("/manage/teams/", {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateTeam(id: number, data: TeamPayload): Promise<Team> {
  return apiRequest<Team>(`/manage/teams/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteTeam(id: number): Promise<void> {
  await apiRequest(`/manage/teams/${id}/`, { method: "DELETE" });
}

// ---------------------------------------------------------------------------
// Team membership API
// ---------------------------------------------------------------------------

export async function addTeamMember(
  teamId: number,
  userId: number,
): Promise<TeamMembership> {
  return apiRequest<TeamMembership>("/manage/team-memberships/", {
    method: "POST",
    body: JSON.stringify({ team: teamId, user: userId }),
  });
}

export async function removeTeamMember(membershipId: number): Promise<void> {
  await apiRequest(`/manage/team-memberships/${membershipId}/`, {
    method: "DELETE",
  });
}

// ---------------------------------------------------------------------------
// Audit log API
// ---------------------------------------------------------------------------

export interface AuditLogFilters {
  user?: number;
  action?: string;
  model_name?: string;
  date_from?: string;
  date_to?: string;
  page?: number;
}

export async function fetchAuditLogs(
  filters: AuditLogFilters = {},
): Promise<PaginatedResponse<AuditLogEntry>> {
  return apiRequest<PaginatedResponse<AuditLogEntry>>(
    `/manage/audit-logs/${buildQueryString(filters)}`,
  );
}
